import React from 'react';
import ContactForm from '../components/ContactForm';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { useSEO } from '../hooks/useSEO';

const FornitoriPage: React.FC = () => {
  useSEO({
    title: 'Diventa Fornitore Synergy - Vendi Materiali Edili Online',
    description: 'Diventa un fornitore Synergy e aumenta i volumi di vendita. Accedi a nuovi clienti, pagamenti più rapidi e una rete di imprese edili certificate.',
    keywords: 'diventa fornitore, vendere materiali edili, fornitori edilizia, rete fornitori, volumi vendita, imprese edilizia, pagamenti rapidi',
    ogTitle: 'Diventa Fornitore Synergy - Vendi Materiali Edili Online',
    ogDescription: 'Diventa un fornitore Synergy e aumenta i volumi di vendita. Accedi a nuovi clienti e pagamenti più rapidi.',
    canonicalUrl: 'https://synergyedilizia.com/fornitori',
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Diventa Fornitore Synergy",
      "description": "Servizio per fornitori di materiali edili per aumentare i volumi di vendita",
      "provider": {
        "@type": "Organization",
        "name": "Synergy"
      },
      "offers": {
        "@type": "Offer",
        "description": "Aumenta i volumi di vendita, amplia la base clienti, pagamenti più rapidi e sicuri"
      }
    }
  });

  const benefits = [
    {
      title: "Aumenta i volumi di vendita",
      description: "Ricevi ordini aggregati e costanti da più clienti.",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
        </svg>
      )
    },
    {
      title: "Amplia la tua base clienti",
      description: "Accedi a nuovi general contractor e imprese.",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      )
    },
    {
      title: "Pagamenti più rapidi e sicuri",
      description: "Riduci i tempi di incasso e ottieni maggiore liquidità.",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    }
  ];

  // Componente per le card dei benefici con animazione
  const BenefitCard: React.FC<{
    benefit: { title: string; description: string; icon: React.ReactNode };
    index: number;
  }> = ({ benefit, index }) => {
    const { elementRef, isVisible } = useScrollAnimation({
      threshold: 0.1,
      triggerOnce: true,
      delay: index * 100
    });

    return (
      <div
        ref={elementRef as React.RefObject<HTMLDivElement>}
        className="bg-white rounded-2xl py-4 md:py-8 px-6 text-center transition-all duration-300"
        style={{
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
          transition: 'opacity 0.6s ease-out, transform 0.6s ease-out',
        }}
      >
        <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center text-primary mb-6 mx-auto">
          {benefit.icon}
        </div>
        <h3 className="text-xl md:text-2xl text-balance font-bold text-gray-900 mb-4">
          {benefit.title}
        </h3>
        <p className="text-base md:text-lg text-balance text-gray-600 leading-relaxed">
          {benefit.description}
        </p>
      </div>
    );
  };

  const scrollToForm = () => {
    const formTitle = document.getElementById('contact-form');
    if (formTitle) {
      formTitle.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
};

  return (
    <div className="pt-20 bg-white">
      {/* Hero Section */}
      <section id="become-supplier" className="py-20 pb-24 md:pb-48 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Diventa un fornitore Synergy
            </h1>
            <p className="text-md sm:text-lg text-gray-600 leading-relaxed text-balance">
              Synergy non è un canale di vendita tradizionale, ma una rete che ti permette di crescere in modo stabile e trasparente.
            </p>
          </div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-12 md:pt-20">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mb-8 md:mb-8 text-center">
              I vantaggi per te:
            </h2>
            <div className="grid md:grid-cols-3 gap-8 mb-8 md:mb-12">
              {benefits.map((benefit, index) => (
                <BenefitCard
                  key={index}
                  benefit={benefit}
                  index={index}
                />
              ))}
            </div>

            <div className="text-center">
              <button 
                onClick={scrollToForm}
                className={`px-8 py-2.5 rounded-full font-semibold text-lg transition-colors duration-300 bg-primary text-white hover:bg-primary/90'`}
                >
                  Candidati come fornitore
                </button>
            </div>
          </div>
        </div>
      </section>

      <ContactForm />
    </div>
  );
};

export default FornitoriPage;
